import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { RouteOff, RefreshCw, Plus, Copy, Check, AlertTriangle, ShieldCheck, Send } from "lucide-react";

const API = "/api";

export default function DeliveryBypass() {
  const [failing, setFailing] = useState([]);
  const [bypassed, setBypassed] = useState([]);
  const [ruleName, setRuleName] = useState("");
  const [loading, setLoading] = useState(true);
  const [newDomain, setNewDomain] = useState("");
  const [adding, setAdding] = useState(false);
  const [copied, setCopied] = useState(false);

  const refresh = useCallback(() => {
    Promise.all([
      axios.get(`${API}/bypass/failing-domains`).catch(() => ({ data: { domains: [] } })),
      axios.get(`${API}/bypass/domains`).catch(() => ({ data: { domains: [], rule_name: "" } })),
    ]).then(([f, b]) => {
      setFailing(f.data.domains || []);
      setBypassed((b.data.domains || []).map((d) => d.domain));
      setRuleName(b.data.rule_name || "");
      setLoading(false);
    });
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const isBypassed = (domain) => bypassed.includes(domain);

  const toggle = async (domain, on) => {
    try {
      if (on) {
        await axios.post(`${API}/bypass/domains`, { domain });
        toast.success(`${domain} will now send DIRECT via Microsoft (apply the PowerShell below)`);
      } else {
        await axios.post(`${API}/bypass/domains/remove`, { domain });
        toast.success(`${domain} removed from bypass — it will route through the relay again`);
      }
      refresh();
    } catch (e) {
      toast.error("Failed: " + (e.response?.data?.detail || e.message));
    }
  };

  const addManual = async () => {
    const d = newDomain.trim().toLowerCase().replace(/^@/, "");
    if (!d || !d.includes(".")) { toast.error("Enter a valid domain, e.g. logipia.co.kr"); return; }
    setAdding(true);
    try {
      await axios.post(`${API}/bypass/domains`, { domain: d });
      toast.success(`${d} added to bypass list`);
      setNewDomain("");
      refresh();
    } catch (e) {
      toast.error("Failed: " + (e.response?.data?.detail || e.message));
    }
    setAdding(false);
  };

  const saveRuleName = async () => {
    try {
      await axios.post(`${API}/bypass/rule-name`, { rule_name: ruleName });
      toast.success("Transport rule name saved");
    } catch (e) {
      toast.error("Failed to save rule name");
    }
  };

  // Union of failing domains + manually-bypassed domains (so toggles always show).
  const failingDomains = failing.map((f) => f.domain);
  const extraBypassed = bypassed
    .filter((d) => !failingDomains.includes(d))
    .map((d) => ({ domain: d, fail_count: 0, recipients: [], recipient_count: 0, last_error: "manually added", last_seen: "", bypassed: true }));
  const rows = [...failing, ...extraBypassed];

  const powershell = ruleName.trim()
    ? `Set-TransportRule -Identity "${ruleName.trim()}" \`\n  -ExceptIfRecipientDomainIs @(${bypassed.map((d) => `"${d}"`).join(", ") || '""'})`
    : `# 1. Find your relay routing rule name:\nGet-TransportRule | ? { $_.RouteMessageOutboundConnector } | fl Name\n\n# 2. Paste the name above, then this updates automatically:\nSet-TransportRule -Identity "<YOUR RELAY RULE NAME>" \`\n  -ExceptIfRecipientDomainIs @(${bypassed.map((d) => `"${d}"`).join(", ") || '""'})`;

  const copyPs = () => {
    navigator.clipboard.writeText(powershell);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
    toast.success("PowerShell copied — paste it into Exchange Online PowerShell");
  };

  const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

  return (
    <div className="max-w-6xl" data-testid="delivery-bypass-page">
      <div className="flex items-start justify-between mb-2">
        <div>
          <h1 className="text-3xl font-semibold text-[#171717] flex items-center gap-2">
            <RouteOff size={26} className="text-[#0F6CBD]" /> Delivery Bypass
          </h1>
          <p className="text-sm text-[#5E5E5E] mt-1 max-w-2xl">
            Some recipient servers block the relay&apos;s IP (e.g. Korean anti-spam gateways), so mail to them fails.
            Toggle a domain to send it <strong>direct via Microsoft</strong> instead — delivered with the original sender, zero failures.
            (Those domains won&apos;t get the injected signature.)
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => { setLoading(true); refresh(); }} data-testid="bypass-refresh" className="h-9">
          <RefreshCw size={14} className={`mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      {/* Step 2: Apply to Microsoft 365 */}
      <div className="mt-6 rounded-lg border border-[#E1DFDD] bg-white p-5" data-testid="apply-m365-panel">
        <div className="flex items-center gap-2 mb-3">
          <ShieldCheck size={18} className="text-[#107C10]" />
          <h2 className="text-base font-semibold text-[#171717]">Apply to Microsoft 365</h2>
          <span className="text-xs text-[#5E5E5E]">— paste this once into Exchange Online PowerShell; re-paste whenever you change the list.</span>
        </div>
        <div className="flex items-center gap-2 mb-3">
          <label className="text-xs text-[#5E5E5E] whitespace-nowrap">Transport rule name:</label>
          <Input
            value={ruleName}
            onChange={(e) => setRuleName(e.target.value)}
            onBlur={saveRuleName}
            placeholder="SignatureForge Route to Relay"
            className="h-8 w-72 text-sm font-mono"
            data-testid="rule-name-input"
          />
          <span className="text-[11px] text-[#5E5E5E]">(the rule that routes mail to your relay)</span>
        </div>
        <div className="relative">
          <pre className="bg-[#1E1E1E] text-[#D4D4D4] text-xs rounded-md p-4 overflow-x-auto font-mono whitespace-pre" data-testid="powershell-output">{powershell}</pre>
          <Button size="sm" onClick={copyPs} data-testid="copy-powershell-btn" className="absolute top-2 right-2 h-7 bg-[#0F6CBD] hover:bg-[#0B5394]">
            {copied ? <Check size={13} className="mr-1" /> : <Copy size={13} className="mr-1" />}{copied ? "Copied" : "Copy"}
          </Button>
        </div>
      </div>

      {/* Problematic domains */}
      <div className="mt-6 rounded-lg border border-[#E1DFDD] bg-white overflow-hidden">
        <div className="flex items-center gap-2 border-b border-[#E1DFDD] bg-[#FAFAFA] px-4 py-3">
          <AlertTriangle size={16} className="text-[#8A6116]" />
          <h2 className="text-sm font-semibold text-[#171717]">Problematic domains</h2>
          <span className="text-xs text-[#5E5E5E]">— recent delivery failures grouped by recipient domain</span>
          <div className="ml-auto flex items-center gap-2">
            <Input
              placeholder="add a domain to bypass…"
              value={newDomain}
              onChange={(e) => setNewDomain(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addManual()}
              className="h-8 w-56 text-sm"
              data-testid="add-domain-input"
            />
            <Button size="sm" onClick={addManual} disabled={adding} data-testid="add-domain-btn" className="h-8">
              <Plus size={13} className="mr-1" />{adding ? "Adding…" : "Add"}
            </Button>
          </div>
        </div>

        {loading && rows.length === 0 ? (
          <div className="p-8 text-center text-sm text-[#5E5E5E]">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-12 text-center" data-testid="bypass-empty">
            <Send size={30} className="mx-auto text-[#107C10] mb-3" strokeWidth={1.4} />
            <p className="text-sm text-[#5E5E5E]">No failing domains right now — everything is delivering.</p>
            <p className="text-xs text-[#5E5E5E] mt-1">Domains that start failing will appear here automatically.</p>
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="bypass-table">
            <thead className="bg-[#FAFAFA] text-[#5E5E5E] text-[11px] uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Domain</th>
                <th className="text-left px-4 py-2 font-medium">Failures</th>
                <th className="text-left px-4 py-2 font-medium">Last error</th>
                <th className="text-left px-4 py-2 font-medium">Recipients</th>
                <th className="text-left px-4 py-2 font-medium">Last seen</th>
                <th className="text-right px-4 py-2 font-medium">Bypass (direct)</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.domain} className="border-t border-[#E1DFDD] hover:bg-[#FAFAFA]" data-testid={`bypass-row-${r.domain}`}>
                  <td className="px-4 py-3 font-mono text-xs text-[#171717]">{r.domain}</td>
                  <td className="px-4 py-3">
                    {r.fail_count > 0 ? (
                      <span className="inline-block bg-[#FED9CC] text-[#A4262C] px-1.5 py-0.5 rounded text-[11px] font-semibold tabular-nums">{r.fail_count}</span>
                    ) : <span className="text-[#5E5E5E] text-xs">—</span>}
                  </td>
                  <td className="px-4 py-3 text-[#5E5E5E] text-xs max-w-[340px] truncate" title={r.last_error}>{r.last_error || "—"}</td>
                  <td className="px-4 py-3 text-[#5E5E5E] text-xs" title={(r.recipients || []).join(", ")}>
                    {r.recipient_count > 0 ? `${r.recipients[0]}${r.recipient_count > 1 ? ` +${r.recipient_count - 1}` : ""}` : "—"}
                  </td>
                  <td className="px-4 py-3 text-[#5E5E5E] text-xs whitespace-nowrap">{fmt(r.last_seen)}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      {isBypassed(r.domain) && <span className="text-[10px] font-semibold text-[#107C10]">DIRECT</span>}
                      <Switch
                        checked={isBypassed(r.domain)}
                        onCheckedChange={(v) => toggle(r.domain, v)}
                        data-testid={`bypass-toggle-${r.domain}`}
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
